import axios from 'axios';
import { ApiResponse } from '../types/models';

// Настройки API
export const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://localhost:5000') + '/api';

// Добавляем обработку ошибок подключения
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // Можно будет сделать настраиваемым

// Создаем axios instance с базовой конфигурацией
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Переменная для хранения текущего контрагента
let currentCompanyId: number | null = null;

export const setCurrentCompanyId = (companyId: number | null) => {
  currentCompanyId = companyId;
};

// Инициализация currentCompanyId из cookies при загрузке
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

(() => {
  try {
    const cookieCompanyId = getCookie('selectedCompanyId');
    const parsed = cookieCompanyId ? parseInt(cookieCompanyId) : null;
    currentCompanyId = Number.isFinite(parsed as number) ? parsed : null;
  } catch (_) {
    currentCompanyId = null;
  }
})();

// Интерцепторы для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error('API Error:', error);

    // Проверяем, является ли ошибка сетевой
    if (error.code === 'ECONNRESET' || error.code === 'ECONNREFUSED' || !error.response) {
      console.warn('Network error detected. Backend server may not be running.');
    }

    return Promise.reject(error);
  }
);

// Вспомогательные функции для работы с API
export const apiHelpers = {
  // Обработка успешного ответа
  handleSuccess: <T>(response: any): ApiResponse<T> => ({
    data: response.data,
    success: true,
  }),

  // Обработка ошибки
  handleError: (error: any): ApiResponse<any> => {
    let message = 'Произошла ошибка';

    if (error.code === 'ECONNRESET' || error.code === 'ECONNREFUSED') {
      message = 'Не удалось подключиться к серверу. Проверьте, запущен ли backend-сервер.';
    } else if (!error.response) {
      message = 'Ошибка сети. Проверьте подключение к интернету и работу сервера.';
    } else if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }

    return {
      data: null,
      success: false,
      message,
    };
  },

  // Добавление companyId к URL, если он установлен
  addCompanyIdToUrl: (url: string): string => {
    if (currentCompanyId && !url.includes('companyId=')) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}companyId=${currentCompanyId}`;
    }
    return url;
  },

  // Получение данных с обработкой ошибок
  get: async <T>(url: string): Promise<ApiResponse<T>> => {
    try {
      const urlWithCompanyId = apiHelpers.addCompanyIdToUrl(url);
      const response = await api.get(urlWithCompanyId);
      return apiHelpers.handleSuccess<T>(response);
    } catch (error) {
      return apiHelpers.handleError(error);
    }
  },

  // Отправка данных
  post: async <T>(url: string, data: any): Promise<ApiResponse<T>> => {
    try {
      const urlWithCompanyId = apiHelpers.addCompanyIdToUrl(url);
      const response = await api.post(urlWithCompanyId, data);
      return apiHelpers.handleSuccess<T>(response);
    } catch (error) {
      return apiHelpers.handleError(error);
    }
  },

  // Обновление данных
  put: async <T>(url: string, data: any): Promise<ApiResponse<T>> => {
    try {
      const urlWithCompanyId = apiHelpers.addCompanyIdToUrl(url);
      const response = await api.put(urlWithCompanyId, data);
      return apiHelpers.handleSuccess<T>(response);
    } catch (error) {
      return apiHelpers.handleError(error);
    }
  },

  // Удаление данных
  delete: async <T>(url: string): Promise<ApiResponse<T>> => {
    try {
      const urlWithCompanyId = apiHelpers.addCompanyIdToUrl(url);
      const response = await api.delete(urlWithCompanyId);
      return apiHelpers.handleSuccess<T>(response);
    } catch (error) {
      return apiHelpers.handleError(error);
    }
  },
};

